/* eslint-disable camelcase */
import { createContext, useContext, useMemo, useState } from 'react';

import useCurrentBox from '@app/hooks/use-current-box';
import useCurrentBoxId from '@app/hooks/use-current-box-id';
import type {
  Column,
  Config,
  Product,
  ProductTableBox,
} from '@flow/libs/data-validators/entity-schemas/document-box/product-table';

import type { FormBody } from '@app/components/contract-boxes/product-table-box/product-table/popup-forms/update-product-helpers';

type Props = {
  agreementId: number;
  boxId: number;
  children: React.ReactNode;
};

type EnabledColumnsKeyLabelMapper = {
  name?: string;
  description?: string;
  price_1?: string;
  price_2?: string;
  count?: string;
};

type ProductData = {
  bodyData: FormBody;
  productId: number | null;
};

type ContextValue = {
  agreementId: Props['agreementId'];
  box: ProductTableBox;
  boxId: Props['boxId'];
  columns: Column[];
  config: Config;
  data: Product[];
  enabledColumnsKeyLabelMapper: EnabledColumnsKeyLabelMapper;
  productData: ProductData;
  setProductData: (data: ProductData) => void;
};

export const ProductTableBoxContext = createContext(null) as unknown as React.Context<ContextValue>;

export const ProductTableBoxContextProvider = (props: Props) => {
  const { agreementId, boxId, children } = props;

  const currentBoxId = useCurrentBoxId(boxId);
  const box = useCurrentBox(currentBoxId) as ProductTableBox;
  const { config } = box;
  const { columns } = config;
  const { data } = box.content;
  const [productData, setProductData] = useState<ProductData>({
    productId: null,
    bodyData: {},
  });

  const enabledColumnsKeyLabelMapper = useMemo(
    () =>
      columns?.reduce((keyLabelMapper: EnabledColumnsKeyLabelMapper, column) => {
        if (!column.enabled) {
          return keyLabelMapper;
        }
        return {
          ...keyLabelMapper,
          [column.key]: column.label || '',
        };
      }, {}),
    [
      columns,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: The memo needs to be memoized
  const contextValue = useMemo(
    () => ({
      agreementId,
      box,
      boxId: currentBoxId,
      columns,
      config,
      data,
      enabledColumnsKeyLabelMapper,
      productData,
      setProductData,
    }),
    [
      agreementId,
      box,
      columns,
      config,
      currentBoxId,
      data,
      enabledColumnsKeyLabelMapper,
      productData,
      setProductData,
    ],
  );

  return (
    <ProductTableBoxContext.Provider value={contextValue}>
      {children}
    </ProductTableBoxContext.Provider>
  );
};

export const useProductTableBoxContext = () => {
  const contextValue = useContext<ContextValue>(ProductTableBoxContext);

  if (!contextValue) {
    throw new Error('useProductTableBox should be used inside a ProductTableBoxContext');
  }

  return contextValue;
};
